/**
 * Utilitários para formatação de eventos de calendário
 */
import moment from 'moment';
import 'moment/locale/pt-br'; // Importa localização em português

/**
 * Formata a data do evento para exibição em diferentes contextos
 * @param {string|Date} start - Data de início do evento
 * @param {string|Date|null} end - Data de fim do evento (opcional)
 * @param {Object} options - Opções de formatação
 * @param {string} options.lang - Idioma (ex: 'pt-br')
 * @param {Date|null} options.contextDate - Data de contexto (para eventos em um mês específico)
 * @returns {string} - String formatada com a data do evento
 */
export const formatEventDate = (start, end, options = {}) => {
  const {
    lang = 'pt-br',
    contextDate = null,
    format = 'default', // 'default', 'list', 'modal'
  } = options;

  if (!start) return '';

  // Configura o idioma para o moment
  moment.locale(lang);

  const startDate = moment(start);
  const endDate = end ? moment(end) : null;
  const currentMonthDate = contextDate ? moment(contextDate) : null;
  const separator = ' - ';

  const isSameDay = endDate && startDate.isSame(endDate, 'day');
  const isSameMonth = endDate && startDate.isSame(endDate, 'month');
  const startInCurrentMonth = currentMonthDate
    ? startDate.isSame(currentMonthDate, 'month')
    : true;
  const endInCurrentMonth = currentMonthDate
    ? endDate && endDate.isSame(currentMonthDate, 'month')
    : true;

  // Escolha do formato baseado no contexto
  let dateFormat;

  if (format === 'modal') {
    // Formatação no estilo: "25 a 28 de maio de 2025" ou "25 de maio a 28 de junho de 2025"
    if (isSameDay || !end) {
      // Para eventos de um único dia
      return startDate.format('DD [de] MMMM [de] YYYY');
    } else if (isSameMonth) {
      // Para eventos no mesmo mês
      const startDay = startDate.format('DD');
      const endDay = endDate.format('DD');
      const monthYear = startDate.format('[de] MMMM [de] YYYY');
      return `${startDay} a ${endDay} ${monthYear}`;
    } else {
      // Para eventos em meses diferentes
      const startDayMonth = startDate.format('DD [de] MMMM');
      const endDayMonth = endDate.format('DD [de] MMMM');
      const startYear = startDate.format('YYYY');
      const endYear = endDate.format('YYYY');

      if (startYear === endYear) {
        // Mesmo ano
        return `${startDayMonth} a ${endDayMonth} de ${startYear}`;
      } else {
        // Anos diferentes
        return `${startDayMonth} de ${startYear} a ${endDayMonth} de ${endYear}`;
      }
    }
  }

  // Para visualização em lista
  if (format === 'list') {
    // Formato para dias no mês atual: apenas o dia
    const dayOnlyFormat = 'DD';

    // Formato para dias em outros meses: mês abreviado/dia
    const dayMonthFormat = lang === 'pt-br' ? 'MMM/DD' : 'MMM/DD';

    // Se for apenas um dia ou não tiver data de fim
    if (isSameDay || !end) {
      return startInCurrentMonth
        ? startDate.format(dayOnlyFormat)
        : startDate.format(dayMonthFormat);
    } else {
      let startStr = startInCurrentMonth
        ? startDate.format(dayOnlyFormat)
        : startDate.format(dayMonthFormat);

      let endStr = endInCurrentMonth
        ? endDate.format(dayOnlyFormat)
        : endDate.format(dayMonthFormat);

      const dateStr = startStr + separator + endStr;

      if (!isSameMonth) {
        return dateStr + ' *';
      }
      return dateStr;
    }
  }

  // Formato padrão
  dateFormat = 'DD/MM/YYYY';

  if (isSameDay || !end) {
    return startDate.format(dateFormat);
  } else {
    return `${startDate.format(dateFormat)}${separator}${endDate.format(dateFormat)}`;
  }
};

/**
 * Componente para renderizar o conteúdo do modal de evento
 * @param {Object} event - Objeto do evento
 * @param {Object} options - Opções de formatação
 * @returns {Object} - Objeto com os dados formatados do evento
 */
export const formatEventForModal = (event, options = {}) => {
  if (!event) return null;

  const { lang = 'pt-br' } = options;

  // Formatação da data para o modal
  const formattedDate = formatEventDate(event.start, event.end, {
    lang,
    format: 'modal',
  });

  return {
    title: event.title || '',
    date: formattedDate,
    description: event.description || '',
    location: event.location || '',
    event_url: event.event_url || '',
    theme: event.theme || '',
  };
};

const EventFormatter = {
  formatEventDate,
  formatEventForModal,
};

export default EventFormatter;
